import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Package,
  Save,
  AlertTriangle,
} from "lucide-react";
import { adminApi, isAdminLoggedIn, verifyAdminToken } from "@/utils/auth";

// Base costume types (physical inventory items)
const COSTUME_TYPES = [
  { name: "Male T-Shirt", price: 280 },
  { name: "Female T-Shirt", price: 280 },
  { name: "Short Sleeve Monokini", price: 320 },
  { name: "Long Sleeve Monokini", price: 320 },
];

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL"];

export default function AdminInventory() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState({});
  const [registered, setRegistered] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAdminLoggedIn()) {
        navigate("/admin/login");
        return;
      }
      const valid = await verifyAdminToken();
      if (!valid) {
        navigate("/admin/login");
        return;
      }
      fetchInventory();
    };
    checkAuth();
  }, [navigate]);

  const fetchInventory = async () => {
    try {
      const response = await adminApi.get("/inventory");
      // Convert array to objects for easier manipulation
      const inventoryMap = {};
      const registeredMap = {};
      response.data.forEach((item) => {
        const key = `${item.package}|${item.size}`;
        inventoryMap[key] = item.quantity;
        registeredMap[key] = item.registered || 0;
      });
      setInventory(inventoryMap);
      setRegistered(registeredMap);
    } catch (error) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (costumeName, size, value) => {
    const key = `${costumeName}|${size}`;
    const quantity = parseInt(value) || 0;
    setInventory((prev) => ({
      ...prev,
      [key]: Math.max(0, quantity),
    }));
  };

  const getQuantity = (costumeName, size) => {
    const key = `${costumeName}|${size}`;
    return inventory[key] || 0;
  };

  const getRegistered = (costumeName, size) => {
    const key = `${costumeName}|${size}`;
    return registered[key] || 0;
  };

  const getAvailable = (costumeName, size) => {
    return Math.max(0, getQuantity(costumeName, size) - getRegistered(costumeName, size));
  };

  const getTotalStock = (costumeName) => {
    return SIZES.reduce((total, size) => total + getQuantity(costumeName, size), 0);
  };

  const getTotalRegistered = (costumeName) => {
    return SIZES.reduce((total, size) => total + getRegistered(costumeName, size), 0);
  };

  const getTotalAvailable = (costumeName) => {
    return SIZES.reduce((total, size) => total + getAvailable(costumeName, size), 0);
  };

  const getGrandTotalStock = () => {
    return COSTUME_TYPES.reduce((total, c) => total + getTotalStock(c.name), 0);
  };

  const getGrandTotalRegistered = () => {
    return COSTUME_TYPES.reduce((total, c) => total + getTotalRegistered(c.name), 0);
  };

  const getGrandTotalAvailable = () => {
    return COSTUME_TYPES.reduce((total, c) => total + getTotalAvailable(c.name), 0);
  };

  const saveInventory = async () => {
    setSaving(true);
    try {
      const inventoryArray = [];
      COSTUME_TYPES.forEach((costume) => {
        SIZES.forEach((size) => {
          inventoryArray.push({
            package: costume.name,
            size: size,
            quantity: getQuantity(costume.name, size),
          });
        });
      });

      await adminApi.put("/inventory", { inventory: inventoryArray });
      toast.success("Inventory saved successfully!");
    } catch (error) {
      toast.error("Failed to save inventory");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-muted-foreground hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white flex items-center gap-3">
                <Package className="w-8 h-8 text-primary" />
                Inventory Management
              </h1>
              <p className="text-muted-foreground">
                Set total stock - Available = Stock - Registered
              </p>
            </div>
          </div>
          <Button
            onClick={saveInventory}
            disabled={saving}
            className="bg-primary hover:bg-primary/90 text-black font-bold"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Inventory"}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass border-white/10">
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Stock</p>
              <p className="text-3xl font-black text-white">{getGrandTotalStock()}</p>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Registered</p>
              <p className="text-3xl font-black text-secondary">{getGrandTotalRegistered()}</p>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Available</p>
              <p className="text-3xl font-black text-[#CCFF00]">{getGrandTotalAvailable()}</p>
            </CardContent>
          </Card>
          <Card className="glass border-white/10">
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Sold Out</p>
              <p className="text-3xl font-black text-[#FF0055]">
                {COSTUME_TYPES.reduce((total, c) => 
                  total + SIZES.filter(s => getAvailable(c.name, s) === 0 && getQuantity(c.name, s) > 0).length
                , 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Info Box */}
        <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
          <p className="text-primary text-sm">
            <strong>Note:</strong> All Inclusive packages share inventory with their base costume type. 
            "All Inclusive T-Shirt" uses Male T-Shirt stock. Monokini packages share with their sleeve type.
          </p>
        </div>

        {/* Low Stock Warning */}
        {COSTUME_TYPES.some(c => SIZES.some(s => {
          const avail = getAvailable(c.name, s);
          return avail > 0 && avail <= 3;
        })) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <p className="text-yellow-500">Some items are running low on available stock (3 or less)</p>
          </motion.div>
        )}

        {/* Inventory Table */}
        <Card className="glass border-white/10 overflow-hidden">
          <CardHeader>
            <CardTitle className="text-white">Stock by Costume Type & Size</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-muted-foreground uppercase tracking-wider text-xs sticky left-0 bg-background z-10 min-w-[180px]">
                      Costume Type
                    </TableHead>
                    {SIZES.map((size) => (
                      <TableHead
                        key={size}
                        className="text-muted-foreground uppercase tracking-wider text-xs text-center min-w-[100px]"
                      >
                        {size}
                      </TableHead>
                    ))}
                    <TableHead className="text-muted-foreground uppercase tracking-wider text-xs text-center bg-muted/20 min-w-[100px]">
                      Totals
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {COSTUME_TYPES.map((costume) => (
                    <TableRow
                      key={costume.name}
                      className="border-white/5 hover:bg-white/5"
                    >
                      <TableCell className="font-medium text-white sticky left-0 bg-background z-10">
                        <div>
                          <p>{costume.name}</p>
                          <p className="text-xs text-muted-foreground">${costume.price}</p>
                        </div>
                      </TableCell>
                      {SIZES.map((size) => {
                        const stock = getQuantity(costume.name, size);
                        const reg = getRegistered(costume.name, size);
                        const avail = getAvailable(costume.name, size);
                        return (
                          <TableCell key={size} className="text-center p-2">
                            <div className="space-y-1">
                              <Input
                                type="number"
                                min="0"
                                value={stock}
                                onChange={(e) =>
                                  handleQuantityChange(costume.name, size, e.target.value)
                                }
                                className={`w-16 h-8 text-center mx-auto text-sm ${
                                  avail === 0 && stock > 0
                                    ? "bg-[#FF0055]/10 border-[#FF0055]/30"
                                    : avail <= 3 && avail > 0
                                    ? "bg-yellow-500/10 border-yellow-500/30"
                                    : "bg-muted/50 border-transparent"
                                }`}
                              />
                              <div className="text-xs">
                                <span className="text-secondary">{reg}</span>
                                <span className="text-muted-foreground"> / </span>
                                <span className={avail === 0 ? "text-[#FF0055]" : avail <= 3 ? "text-yellow-500" : "text-[#CCFF00]"}>
                                  {avail}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center bg-muted/20">
                        <div className="space-y-1">
                          <p className="font-bold text-white">{getTotalStock(costume.name)}</p>
                          <div className="text-xs">
                            <span className="text-secondary">{getTotalRegistered(costume.name)}</span>
                            <span className="text-muted-foreground"> / </span>
                            <span className="text-[#CCFF00]">{getTotalAvailable(costume.name)}</span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Color Legend */}
        <div className="mt-6 flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-secondary font-bold">Pink #</span>
            <span className="text-muted-foreground">= Registered</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#CCFF00] font-bold">Green #</span>
            <span className="text-muted-foreground">= Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-[#FF0055]/20 border border-[#FF0055]/30"></div>
            <span className="text-muted-foreground">Sold Out</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-500/20 border border-yellow-500/30"></div>
            <span className="text-muted-foreground">Low Stock (1-3)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
